<template>
  <form class="auth-step" @submit.prevent="$emit('submit')">
    <h4 class="mb-3">Sign up</h4>

    <div class="form-inner two mb-20">
      <label>Full Name *</label>
      <input
        :value="signupDraft.fullName"
        type="text"
        placeholder="Enter full name"
        @input="updateField('fullName', $event.target.value)"
      >
      <div v-if="fieldErrors.fullName" class="field-error">{{ fieldErrors.fullName }}</div>
    </div>

    <div class="form-inner two mb-20">
      <label>Mobile No. *</label>
      <div class="mobile-input-row">
        <span class="mobile-prefix">+91</span>
        <input
          :value="mobileDigits"
          type="tel"
          placeholder="Enter mobile number"
          maxlength="10"
          @input="onMobileInput"
        >
      </div>
      <div v-if="fieldErrors.mobile" class="field-error">{{ fieldErrors.mobile }}</div>
    </div>

    <div class="form-inner two mb-20">
      <label>Email address</label>
      <input
        :value="signupDraft.email"
        type="email"
        placeholder="Enter email"
        @input="updateField('email', $event.target.value)"
      >
      <div v-if="fieldErrors.email" class="field-error">{{ fieldErrors.email }}</div>
    </div>

    <div class="form-inner two mb-20">
      <label>Create Password *</label>
      <div class="password-wrapper">
        <input
          :value="signupDraft.password"
          :type="showPassword ? 'text' : 'password'"
          placeholder="Password"
          autocomplete="new-password"
          @input="updateField('password', $event.target.value)"
        >
        <button type="button" class="password-toggle" @click="showPassword = !showPassword">
          <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
        </button>
      </div>
      <div class="password-hint">Min 8 characters, one uppercase letter, one special character.</div>
      <div v-if="fieldErrors.password" class="field-error">{{ fieldErrors.password }}</div>
    </div>

    <div class="form-inner two mb-20">
      <label>Confirm Password *</label>
      <div class="password-wrapper">
        <input
          :value="signupDraft.passwordConfirmation"
          :type="showConfirmPassword ? 'text' : 'password'"
          placeholder="Confirm Password"
          autocomplete="new-password"
          @input="updateField('passwordConfirmation', $event.target.value)"
        >
        <button type="button" class="password-toggle" @click="showConfirmPassword = !showConfirmPassword">
          <i :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
        </button>
      </div>
      <div v-if="fieldErrors.passwordConfirmation" class="field-error">{{ fieldErrors.passwordConfirmation }}</div>
    </div>

    <button type="submit" class="primary-btn1 w-100 mb-20" :disabled="busy">
      <span>{{ busyText }}</span>
    </button>

    <div class="text-center mb-10">or</div>

    <button type="button" class="primary-btn1 black-bg w-100" @click="$emit('switch-mode')">
      <span>Back to Log in</span>
    </button>
  </form>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  signupDraft: {
    type: Object,
    required: true
  },
  fieldErrors: {
    type: Object,
    default: () => ({})
  },
  status: {
    type: String,
    default: 'idle'
  },
  errorMessage: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['submit', 'switch-mode', 'update:signupDraft'])

const busy = computed(() => ['signing_up', 'validating_mobile', 'sending_otp'].includes(props.status))
const busyText = computed(() => {
  if (props.status === 'validating_mobile') return 'Validating...'
  if (props.status === 'sending_otp') return 'Sending OTP...'
  if (props.status === 'signing_up') return 'Creating Account...'
  return 'Continue'
})
const showPassword = ref(false)
const showConfirmPassword = ref(false)

const mobileDigits = computed(() => {
  const digits = String(props.signupDraft.mobile ?? '').replace(/\D/g, '')
  return digits.slice(0, 10)
})

const onMobileInput = (event) => {
  const digits = String(event.target.value ?? '').replace(/\D/g, '').slice(0, 10)
  updateField('mobile', digits)
}

const updateField = (field, value) => {
  emit('update:signupDraft', {
    ...props.signupDraft,
    [field]: value
  })
}
</script>

<style scoped>
.field-error {
  color: #d93025;
  font-size: 12px;
  margin-top: 4px;
}

.password-hint {
  color: #888;
  font-size: 12px;
  margin-top: 4px;
}

.password-wrapper {
  position: relative;
}

.password-wrapper input {
  padding-right: 40px;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: none;
  color: #888;
  cursor: pointer;
  padding: 0;
  font-size: 16px;
  line-height: 1;
}

.password-toggle:hover {
  color: #333;
}

.mobile-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-prefix {
  font-size: 16px;
  font-weight: 500;
  color: var(--title-color, #222);
  flex-shrink: 0;
  line-height: 56px;
}
</style>
